import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "app/store";
import { useTranslation } from "react-i18next";
import { clearAllUnsavedChanges, selectHasUnsavedChanges } from "shared/slices/unsavedChangesSlice";

export const useUnsavedChangesPrompt = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);

    const unsavedRef = useRef(false);
    useEffect(() => {
        unsavedRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    // Warn on tab close or page refresh
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!unsavedRef.current) return;
            e.preventDefault();
            e.returnValue = ""; // Chrome/Edge
            return "";          // Safari/Firefox fallback
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        window.onbeforeunload = handleBeforeUnload;
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.onbeforeunload = null;
        };
    }, []);

    // Confirm internal navigation
    const confirmNavigation = (): boolean => {
        if (
            unsavedRef.current &&
            !window.confirm(t("You have unsaved changes. Leave this page?")!)
        )
            return false;

        dispatch(clearAllUnsavedChanges());
        return true;
    };

    // Guard MUI Dialog onClose
    const guardedClose = <E,>(originalOnClose?: (event: E, reason?: "backdropClick" | "escapeKeyDown") => void) =>
        (event: E, reason?: "backdropClick" | "escapeKeyDown") => {
            if (
                unsavedRef.current &&
                !window.confirm(t("You have unsaved changes. Close the dialog anyway?")!)
            )
                return;
            dispatch(clearAllUnsavedChanges())
            originalOnClose?.(event, reason);
        };

    // Guard any action with unsaved changes
    const guardedAction = (fn?: () => void) => () => {
        if (
            unsavedRef.current &&
            !window.confirm(t("You have unsaved changes. Close the dialog anyway?")!)
        )
            return;
        dispatch(clearAllUnsavedChanges())
        fn?.();
    };

    return { confirmNavigation, guardedClose, guardedAction };
};